import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  ClipboardList,
  Sparkles,
  Rocket,
  ArrowLeft,
  Loader2,
  User,
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useTenant } from '../context/TenantContext';
import { upsertProfile } from '../lib/advisorProfile';
import { useNavigateWithParams } from '../hooks/useNavigateWithParams';
import { ONBOARDING_SKIP_KEY } from '../hooks/useOnboarding';

export function Onboarding() {
  const { locationId, userName } = useTenant();
  const navigate = useNavigateWithParams();
  const [screen, setScreen] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    advisor_name: userName || '',
    company_name: '',
    bio: '',
    advisor_headshot: '',
  });

  const canContinue = form.advisor_name.trim() && form.company_name.trim();

  const handleSkipForNow = () => {
    try {
      sessionStorage.setItem(ONBOARDING_SKIP_KEY, 'true');
    } catch {
      // ignore — fallthrough is fine, gate will just bounce again
    }
    navigate('/');
  };

  const handleSaveAndContinue = async () => {
    if (!canContinue) return;
    setSaving(true);
    setError(null);
    try {
      await upsertProfile({
        location_id: locationId,
        advisor_name: form.advisor_name.trim(),
        company_name: form.company_name.trim(),
        bio: form.bio.trim(),
        advisor_headshot: form.advisor_headshot.trim(),
      });
      setScreen(1);
    } catch (err) {
      setError('Failed to save your profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const steps = [
    {
      icon: ClipboardList,
      title: 'Answer 6 questions',
      description: 'Tell us about your practice and what makes you different',
    },
    {
      icon: Sparkles,
      title: 'AI builds your package',
      description: 'Complete webinar script, emails, social posts, and landing page',
    },
    {
      icon: Rocket,
      title: 'Launch to your audience',
      description: 'Deploy everything to your CRM with one click',
    },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center p-4 md:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-xl space-y-6"
      >
        {/* Progress dots */}
        <div className="flex justify-center gap-2">
          {[0, 1].map((i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === screen ? 'bg-primary-400' : 'bg-slate-600'
              }`}
            />
          ))}
        </div>

        <AnimatePresence mode="wait">
          {screen === 0 ? (
            <motion.div
              key="setup"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25 }}
            >
              <Card className="p-6 md:p-8 space-y-6">
                <div className="text-center space-y-2">
                  <div className="w-12 h-12 mx-auto rounded-xl bg-primary-500/10 flex items-center justify-center">
                    <User className="w-6 h-6 text-primary-400" />
                  </div>
                  <h1 className="text-2xl font-bold text-slate-100">
                    Let's set you up
                  </h1>
                  <p className="text-sm text-slate-400">
                    This only takes a moment
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-100 mb-1.5">
                      Your name *
                    </label>
                    <input
                      type="text"
                      value={form.advisor_name}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, advisor_name: e.target.value }))
                      }
                      placeholder="John Smith"
                      className="w-full px-4 py-2.5 rounded-xl bg-slate-900/80 border border-slate-600 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary-500/50"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-100 mb-1.5">
                      Company name *
                    </label>
                    <input
                      type="text"
                      value={form.company_name}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, company_name: e.target.value }))
                      }
                      placeholder="Smith Financial Group"
                      className="w-full px-4 py-2.5 rounded-xl bg-slate-900/80 border border-slate-600 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary-500/50"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-100 mb-1.5">
                      Headshot URL
                    </label>
                    <input
                      type="text"
                      value={form.advisor_headshot}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          advisor_headshot: e.target.value,
                        }))
                      }
                      placeholder="https://example.com/your-photo.jpg"
                      className="w-full px-4 py-2.5 rounded-xl bg-slate-900/80 border border-slate-600 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary-500/50"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Optional — you can add this later in Settings
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-100 mb-1.5">
                      Short bio
                    </label>
                    <textarea
                      value={form.bio}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, bio: e.target.value }))
                      }
                      placeholder="A few sentences about your practice..."
                      rows={3}
                      className="w-full px-4 py-2.5 rounded-xl bg-slate-900/80 border border-slate-600 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary-500/50 resize-none"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Optional — used in your webinar materials
                    </p>
                  </div>
                </div>

                {error && (
                  <p className="text-sm text-red-400 text-center">{error}</p>
                )}

                <Button
                  size="lg"
                  className="w-full"
                  onClick={handleSaveAndContinue}
                  disabled={!canContinue || saving}
                >
                  {saving ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </Button>

                <button
                  type="button"
                  onClick={handleSkipForNow}
                  className="block mx-auto text-sm text-slate-500 hover:text-slate-300 underline-offset-4 hover:underline transition-colors"
                >
                  Skip for now — I'll set this up in Settings
                </button>
              </Card>
            </motion.div>
          ) : (
            <motion.div
              key="explainer"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25 }}
            >
              <Card className="p-6 md:p-8 space-y-6">
                <div className="text-center space-y-2">
                  <h1 className="text-2xl font-bold text-slate-100">
                    Here's how it works
                  </h1>
                  <p className="text-sm text-slate-400">
                    Takes about 10-15 minutes. Your answers are saved
                    automatically.
                  </p>
                </div>

                <div className="space-y-4">
                  {steps.map((step, index) => (
                    <motion.div
                      key={step.title}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + index * 0.1 }}
                      className="flex items-start gap-4 p-4 rounded-xl bg-slate-800/50"
                    >
                      <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                        <step.icon className="w-5 h-5 text-primary-400" />
                      </div>
                      <div>
                        <p className="text-slate-100 font-medium">
                          {step.title}
                        </p>
                        <p className="text-sm text-slate-400">
                          {step.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="secondary"
                    onClick={() => setScreen(0)}
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back
                  </Button>
                  <Button
                    size="lg"
                    className="flex-1"
                    onClick={() => navigate('/question/1')}
                  >
                    Let's Go
                    <Rocket className="w-5 h-5" />
                  </Button>
                </div>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
